import { useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";

import { assertNativeError } from "@examspark/common";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  Form,
  Modal,
  ModalProps,
  SubmitButtons,
  Text,
  TextInput,
  VSpace,
} from "@examspark/common-ui";
import { Classroom, CourseCalendar } from "@examspark/types";

import { useQueryGetClassroomCourseCalendar } from "../../api/operations/calendar";
import {
  useMutationArchiveClassroomAction,
  useMutationDeleteClassroomAction,
  useMutationResetClassroomAction,
  useMutationUpdateClassroomSettings,
} from "../../api/operations/classroom";
import { useAppContext } from "../../providers/AppContext";

import CourseCalendarSelect from "./CourseCalendarSelect";

const ClassroomSettingsModal = (
  props: ModalProps & {
    classroom: Classroom;
    hideCalendarSelect?: boolean;
    onUpdate?: () => void;
    onDelete?: () => void;
    onReset?: () => void;
  },
) => {
  const [name, setName] = useState(props.classroom.name ?? "");
  const [sectionNumber, setSectionNumber] = useState(
    props.classroom.sectionNumber ?? "",
  );
  const [room, setRoom] = useState(props.classroom.room ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [calendar, setCalendar] = useState<CourseCalendar>();

  const flags = useFlags();
  const [state] = useAppContext();

  const queryClient = useQueryClient();
  const archiveClassroomMutation = useMutationArchiveClassroomAction();
  const resetClassroomMutation = useMutationResetClassroomAction();
  const deleteClassroomMutation = useMutationDeleteClassroomAction();
  const updateClassroomMutation = useMutationUpdateClassroomSettings();

  const currentCalendarQuery = useQueryGetClassroomCourseCalendar(
    props.classroom,
  );

  const classroomId = props.classroom?.id;
  const userId = state.user?.id;

  useEffect(() => {
    if (!currentCalendarQuery.data) return;
    setCalendar(currentCalendarQuery.data);
  }, [currentCalendarQuery.data]);

  return (
    <Modal
      title="Classroom Settings"
      buttons={
        <SubmitButtons
          submitLabel="Save Settings"
          isSubmitting={isSaving}
          onSubmit={async () => {
            setIsSaving(true);
            try {
              if (name.trim().length < 3) {
                throw new Error(
                  "Classroom name must be at least 3 characters long",
                );
              }

              await updateClassroomMutation.mutateAsync({
                ...props.classroom,
                name: name.trim(),
                room: room.trim(),
                sectionNumber: sectionNumber.trim(),
                calendarId: calendar
                  ? [calendar.id, calendar.sk].join("/")
                  : null,
              });
              props.onUpdate?.();
            } catch (err) {
              console.error(err);
              assertNativeError(err);
              alert(err.message);
            } finally {
              setIsSaving(false);
            }
          }}
          onCancel={props.onRequestClose}
        />
      }
      {...props}
    >
      <Modal.PaddedBody>
        <Form.FieldList>
          <Form.Field label="Name">
            <TextInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!props.classroom.isArchived}
            />
          </Form.Field>
          <Form.Field label="Section Number">
            <TextInput
              type="text"
              value={sectionNumber}
              onChange={(e) => setSectionNumber(e.target.value)}
              disabled={!!props.classroom.isArchived}
            />
          </Form.Field>
          <Form.Field label="Room Number">
            <TextInput
              type="text"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              disabled={!!props.classroom.isArchived}
            />
          </Form.Field>
          {!props.hideCalendarSelect && (
            <Form.Field label="Calendar">
              <CourseCalendarSelect
                course={props.classroom.course ?? undefined}
                value={calendar ?? undefined}
                onChange={setCalendar}
                includeArchivedCalendars
                disabled={!!props.classroom.isArchived}
              />
            </Form.Field>
          )}
        </Form.FieldList>
        <div style={{ marginTop: "2rem" }}>
          {props.classroom.isArchived ? (
            <Button
              title="Restore Classroom"
              stretch
              variant="outlined"
              onClick={async () => {
                await ConfirmModal.show({
                  onResolving: async (result) => {
                    if (result) {
                      if (!userId) {
                        console.error("No user ID");
                        return;
                      }
                      if (!classroomId) {
                        console.error("No classroom ID");
                        return;
                      }
                      try {
                        await archiveClassroomMutation.mutateAsync({
                          classroomId,
                          isArchived: false,
                        });
                        queryClient.removeQueries({
                          queryKey: ["classrooms", userId],
                        });
                        props.onDelete?.();
                      } catch {
                        //
                      }
                    }
                  },
                  children: (
                    <CautionBanner variant="plain">
                      Are you sure you want to restore this classroom to your
                      active classroom list?
                    </CautionBanner>
                  ),
                });
              }}
            >
              Restore Classroom
            </Button>
          ) : (
            <Button
              title="Archive Classroom"
              stretch
              variant="outlined"
              onClick={async () => {
                await ConfirmModal.show({
                  onResolving: async (result) => {
                    if (result) {
                      if (!userId) {
                        console.error("No user ID");
                        return;
                      }
                      if (!classroomId) {
                        console.error("No classroom ID");
                        return;
                      }
                      try {
                        await archiveClassroomMutation.mutateAsync({
                          classroomId,
                          isArchived: true,
                        });
                        queryClient.removeQueries({
                          queryKey: ["classrooms", userId],
                        });
                        props.onDelete?.();
                      } catch {
                        //
                      }
                    }
                  },
                  children: (
                    <CautionBanner variant="plain">
                      Are you sure you want to archive this classroom? This will
                      remove the classroom from your list of active classrooms,
                      but it will not delete any data.
                    </CautionBanner>
                  ),
                });
              }}
            >
              Archive Classroom
            </Button>
          )}
          {flags.classroomEnableDestructiveActions && (
            <>
              <Text.H2 style={{ color: "red", margin: "1rem 0" }}>
                Danger Zone
              </Text.H2>
              <VSpace style={{ gap: "0.5rem" }}>
                <Text.Body>
                  These actions permanently destroy classroom data. These are
                  suitable for test classrooms or classrooms that are no longer
                  needed. Please use with caution.
                </Text.Body>
                <Button
                  title="Reset Classroom"
                  stretch
                  color="danger"
                  disabled={!!props.classroom.isArchived}
                  variant="outlined"
                  onClick={async () => {
                    await ConfirmModal.show({
                      onResolving: async (result) => {
                        if (result) {
                          if (!classroomId) {
                            console.error("No classroom ID");
                            return;
                          }
                          await resetClassroomMutation.mutateAsync({
                            classroomId,
                          });
                          props.onReset?.();
                        }
                      },
                      children: (
                        <CautionBanner variant="plain">
                          Are you sure you want to reset this classroom?
                        </CautionBanner>
                      ),
                    });
                  }}
                >
                  Reset Classroom
                </Button>
                <Button
                  title="Delete Classroom"
                  stretch
                  color="danger"
                  variant="outlined"
                  onClick={async () => {
                    await ConfirmModal.show({
                      onResolving: async (result) => {
                        if (result) {
                          if (!userId) {
                            console.error("No user ID");
                            return;
                          }
                          if (!classroomId) {
                            console.error("No classroom ID");
                            return;
                          }
                          await deleteClassroomMutation.mutateAsync({
                            userId,
                            classroomId,
                          });
                          queryClient.removeQueries({
                            queryKey: ["classrooms", userId],
                          });
                          props.onDelete?.();
                        }
                      },
                      children: (
                        <CautionBanner variant="plain">
                          Are you sure you want to permanently delete this
                          classroom? This action cannot be undone.
                        </CautionBanner>
                      ),
                    });
                  }}
                >
                  Delete Classroom
                </Button>
              </VSpace>
            </>
          )}
        </div>
      </Modal.PaddedBody>
    </Modal>
  );
};

export default ClassroomSettingsModal;
